import React, { useState } from 'react';
import { Map, Layers, Download, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализированной информации о маршруте
 */
const RouteDetails = ({ routeDetails, results, showRouteSummary, setShowRouteSummary }) => {
  const [activeTab, setActiveTab] = useState('timeline');
  
  if (!routeDetails || routeDetails.length === 0 || !results) {
    return null;
  }

  // Форматирование времени
  const formatTime = (date) => {
    return date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
  };

  // Определяем типы сегментов
  const getSegmentTypeIcon = (type) => {
    switch(type) {
      case 'start':
        return <div className="w-4 h-4 rounded-full bg-green-500"></div>;
      case 'finish':
        return <div className="w-4 h-4 rounded-full bg-red-500"></div>;
      case 'break':
        return <div className="w-4 h-4 rounded-full bg-yellow-500"></div>;
      case 'stop':
        return <div className="w-4 h-4 rounded-full bg-blue-500"></div>;
      default:
        return <div className="w-4 h-4 rounded-full bg-gray-500"></div>;
    }
  };

  const getSegmentTypeName = (type) => {
    switch(type) {
      case 'start': return 'Отправление';
      case 'finish': return 'Прибытие';
      case 'break': return 'Перерыв';
      case 'stop': return 'Остановка';
      default: return 'Точка';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Map className="w-5 h-5 mr-2 text-blue-500" />
          Детали маршрута
        </h3>

        {/* Кнопка переключения полного/краткого отображения */}
        <Button
          size="sm"
          onClick={() => setShowRouteSummary(prev => !prev)}
          variant="outline"
          aria-expanded={showRouteSummary}
        >
          {showRouteSummary ? 'Показать основные точки' : 'Показать полный маршрут'}
        </Button>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'timeline'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Временная шкала
        </button>
        <button
          onClick={() => setActiveTab('table')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'table'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Табличный вид
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Подробная информация о маршруте с указанием времени и расстояния для каждой ключевой точки.</p>
        </div>
      </div>

      {/* Временная шкала */}
      {activeTab === 'timeline' && (
        <div className="mb-4">
          <div className="relative pl-8 ml-4 border-l-2 border-blue-300 dark:border-blue-700">
            {routeDetails.map((segment, index) => (
              <div 
                key={index} 
                className={`mb-6 relative ${
                  showRouteSummary || segment.type === 'start' || segment.type === 'finish' 
                  || segment.type === 'stop' ? '' : 'hidden'
                }`}
              >
                <div className="absolute -left-10 mt-1">
                  {getSegmentTypeIcon(segment.type)}
                </div>
                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatTime(segment.time)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {segment.time.toLocaleDateString('ru-RU', {day: 'numeric', month: 'short'})}
                  </span>
                  <span className="text-xs py-0.5 px-2 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
                    {formatNumber(segment.distance, 1)} км
                  </span>
                </div>
                <div className="text-base font-medium mb-0.5">
                  {segment.location}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {segment.description}
                  {segment.duration && 
                    <span className="ml-2 text-blue-600 dark:text-blue-400">
                      (Длительность: {segment.duration} мин)
                    </span>
                  }
                </div>
                
                {/* Отображаем линию маршрута между точками */}
                {index < routeDetails.length - 1 && (
                  <div className="absolute top-0 left-0 w-full h-full flex justify-center">
                    <div className="h-full border-l-2 border-dashed border-gray-300 dark:border-gray-700 ml-4"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Табличный вид */}
      {activeTab === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[500px] route-details-table">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[600px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold">Тип</th>
                <th scope="col" className="px-3 py-2 font-semibold">Место</th>
                <th scope="col" className="px-3 py-2 font-semibold">Время</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Расстояние</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Длительность</th>
                <th scope="col" className="px-3 py-2 font-semibold">Описание</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {routeDetails
                .filter(segment => showRouteSummary || segment.type === 'start' || segment.type === 'finish' || segment.type === 'stop')
                .map((segment, index) => (
                <tr
                  key={index}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 ${
                    segment.type === 'start' ? 'bg-green-50 dark:bg-green-900/20' : 
                    segment.type === 'finish' ? 'bg-red-50 dark:bg-red-900/20' :
                    segment.type === 'break' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                    segment.type === 'stop' ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <td className="px-3 py-2 font-medium">
                    <div className="flex items-center gap-2">
                      {getSegmentTypeIcon(segment.type)}
                      <span>{getSegmentTypeName(segment.type)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{segment.location}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {segment.time.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}
                  </td>
                  <td className="px-3 py-2 text-right">{formatNumber(segment.distance, 1)} км</td>
                  <td className="px-3 py-2 text-right">
                    {segment.duration ? `${segment.duration} мин` : '-'}
                  </td>
                  <td className="px-3 py-2">{segment.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Легенда */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          <span>Отправление</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          <span>Прибытие</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
          <span>Перерывы</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
          <span>Остановки</span>
        </div>
      </div>

      {/* Кнопка экспорта */}
      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => window.print()} // Простая заглушка, настоящая функция экспорта уже реализована в CalculationForm
          size="sm"
          icon={<Download className="w-4 h-4" />}
          variant="ghost"
        >
          Экспорт маршрута
        </Button>
      </div>
    </div>
  )
}

export default RouteDetails;